/**
 * DeterminationStates.tsx — a determination, drawn so it cannot mislead.
 *
 * The visual contract, and it is a contract because the failure is human:
 *
 *   • A settled Engine-asserted determination (affirmative or adverse) is the
 *     ONLY case that gets a colour that reads as settled.
 *   • Every other case is drawn in the neutral treatment, and carries the
 *     Engine's basis text next to it, so a reviewer is told WHY the drawing
 *     declines to conclude rather than being left to infer it from a colour.
 *   • `not-reported` and `unrecognised` are drawn differently from each other,
 *     because they mean different things: one is a silent Engine, the other is a
 *     value this browser does not understand. Both are absences; neither is a
 *     result.
 *
 * There is no `default:` that returns something safe-looking. A new case
 * defaults to `indeterminate`, which is the correct direction for a safety
 * vocabulary: an unhandled case must understate rather than overstate.
 */

import type { JsonSchemaFragment } from "../../api/capabilityManifest";
import { Panel } from "../../components/Panel";
import {
  readEnumeration,
  resolveDetermination,
  type Determination,
  type Treatment,
} from "./determination";

/** A payload's property value, keyed by property name. */
export type DeterminationPayload = Readonly<Record<string, unknown>>;

function classFor(treatment: Treatment): string {
  switch (treatment) {
    // The only two classes that assert anything. Anything else renders neutral,
    // which is why the default below is deliberately `indeterminate` and not a
    // settled treatment.
    case "affirmative": return "determination determination-affirmative";
    case "adverse": return "determination determination-adverse";
    case "indeterminate": return "determination determination-indeterminate";
    case "not-reported": return "determination determination-absent";
    case "unrecognised": return "determination determination-unrecognised";
    default: return "determination determination-indeterminate";
  }
}

/**
 * The plain-language reading of a treatment.
 *
 * These strings are about PRESENTATION — what the drawing is claiming — and
 * name no determination value, because the values are the Engine's and this
 * repository does not hold a copy of them.
 */
function statement(treatment: Treatment): string {
  switch (treatment) {
    case "affirmative": return "The Engine asserts this.";
    case "adverse": return "The Engine asserts the opposite of this.";
    case "indeterminate": return "Reported, and undecided.";
    case "not-reported": return "No determination reported.";
    case "unrecognised": return "A value this browser does not understand. No conclusion is drawn.";
    default: return "Undetermined.";
  }
}

function DeterminationRow({ name, determination }: { name: string; determination: Determination }): JSX.Element {
  return (
    <li className={classFor(determination.treatment)}>
      <span className="determination-name">{name}</span>
      <span className="determination-value">{determination.label ?? "—"}</span>
      <span className="determination-statement">{statement(determination.treatment)}</span>
      {determination.requiresAttention && <span className="muted determination-basis">{determination.basis}</span>}
    </li>
  );
}

/**
 * Render every determination property a descriptor's result schema declares.
 *
 * Properties are taken from the Engine's own `resultSchema`, so a determination
 * this browser has never heard of is rendered as soon as the Engine publishes
 * it — with no edit here. Properties the payload did not carry are STILL
 * rendered, in the `not-reported` treatment: silently omitting them is how an
 * absent determination becomes an invisible one, and invisible is indistinguishable
 * from settled at a glance.
 */
/**
 * Whether a property is a DETERMINATION — a state the Engine enumerates.
 *
 * The test is the Engine's own published enumeration, and nothing else. A closed
 * vocabulary is what makes a property a state machine: it says the Engine can hold
 * one of a known set of positions, and therefore that the absence of one is a
 * statement rather than an oversight.
 *
 * The exclusions are load-bearing, not tidiness. An identity, a reference, an
 * acceptance flag and a refusal list are all real properties of the same result,
 * and drawing them as "Reported, and undecided" would be actively misleading — it
 * would put a safety-absence statement next to a boolean that is simply true.
 * Those properties are shown in full by the read-only value view instead, which is
 * the right place for a fact that is not a determination.
 */
function isDetermination(schema: JsonSchemaFragment | undefined): boolean {
  if (schema === undefined || schema.type !== "string") return false;
  const enumeration = readEnumeration(schema);
  return enumeration !== undefined && enumeration.values.length > 0;
}

export function DeterminationStates({ resultSchema, payload, title = "Determination" }: {
  resultSchema: JsonSchemaFragment | undefined;
  payload: DeterminationPayload;
  title?: string;
}): JSX.Element | null {
  const properties = resultSchema?.properties;
  if (properties === undefined) return null;
  const names = Object.keys(properties).filter(name => isDetermination(properties[name])).sort();
  // Nothing here is a determination. An empty panel would be noise, and the
  // non-determination properties are shown in full by the read-only value view,
  // which is the right place for a fact that is not a determination.
  if (names.length === 0) return null;

  return (
    <Panel title={title}>
      <ul className="determinations">
        {names.map(name => (
          <DeterminationRow
            key={name}
            name={name}
            determination={resolveDetermination(properties[name], payload[name])}
          />
        ))}
      </ul>
      <p className="muted">
        Every property the Engine's result schema declares is listed, including the ones this payload did not
        carry. An absent determination is drawn as an absence, never as a result.
      </p>
    </Panel>
  );
}
